/**
 * iter-338z5 — AI Preset Library.
 *
 * Studio-owned catalogue of reusable AI Task Creator presets. Owners/admins
 * can browse usage counts, edit the templates (title / description / rates
 * / category), toggle favourites, and remove presets no longer in use.
 *
 * Data source: `/api/boq-ai-presets` (list / put / delete / toggle-favourite).
 * Same collection consumed by `AITaskCreatorDialog` — edits here immediately
 * change the preview that "custom:{id}" produces inside Edit BOQ.
 */
import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import {
  BookmarkPlus, Star, Search, Pencil, Trash2, Loader2, Save,
  Sparkles, TrendingUp, Calendar, Copy, Download, FileSpreadsheet, Send,
} from 'lucide-react';
import { formatDate } from '@/utils/dateFormat';
import CategoryDropdown from '@/components/boq/CategoryDropdown';
import UnitSelect from '@/components/boq/UnitSelect';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyDraft = {
  name: '', task_title: '', description: '',
  main_category_name: '', sub_category_name: '',
  unit: 'NOS', quantity: 1,
  vendor_rate: 0, client_rate: 0, gst_percent: 18,
  remarks: '',
  // iter-386u-bc — structured materials & technical specs
  materials: [],
};

export default function AIPresetLibraryPage() {
  const [presets, setPresets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  // iter-383 — category legend filter (option 5-a+c). Empty string = show
  // all categories. Only chips with ≥1 preset are rendered.
  const [categoryFilter, setCategoryFilter] = useState('');
  // iter-386u-bd — Material-category filter (Hardware / Finishes / …)
  const [materialCategoryFilter, setMaterialCategoryFilter] = useState('');
  // iter-386u-bf — Presets vs Marketplace tab
  const [tab, setTab] = useState('presets');
  const [packs, setPacks] = useState([]);
  const [installingPack, setInstallingPack] = useState('');
  const loadPacks = async () => {
    try {
      const r = await axios.get(`${API}/boq-ai-presets/marketplace`, { headers });
      setPacks(r.data?.packs || []);
    } catch { setPacks([]); }
  };
  useEffect(() => { if (tab === 'marketplace') loadPacks(); }, [tab]);
  const installPack = async (code) => {
    setInstallingPack(code);
    try {
      const r = await axios.post(`${API}/boq-ai-presets/marketplace/${code}/install`, {}, { headers });
      toast.success(`Installed ${r.data?.imported} materials into your library`);
      await fetchPresets();
      await loadPacks();
    } catch (e) {
      // iter-386u-bh — surface the real backend error so admins know why
      const detail = e.response?.data?.detail;
      toast.error(detail ? `Install failed: ${typeof detail === 'string' ? detail : JSON.stringify(detail)}` : `Install failed: ${e.message || 'unknown error'}`);
      console.error('Pack install failed:', e.response || e);
    }
    finally { setInstallingPack(''); }
  };
  const [editing, setEditing] = useState(null);   // preset row being edited (null = closed)
  const [draft, setDraft] = useState(emptyDraft);
  const [saving, setSaving] = useState(false);
  // iter-338z7 — Import from BOQ line
  const [importOpen, setImportOpen] = useState(false);
  const [importSearch, setImportSearch] = useState('');
  const [importCandidates, setImportCandidates] = useState([]);
  const [importLoading, setImportLoading] = useState(false);
  // iter-338z9 — Bulk apply preset to BOQs
  const [bulkPreset, setBulkPreset] = useState(null);     // preset being applied
  const [bulkWorksheets, setBulkWorksheets] = useState([]);
  const [bulkSelected, setBulkSelected] = useState(new Set());
  const [bulkInstruction, setBulkInstruction] = useState('');
  const [bulkApplying, setBulkApplying] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);

  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  const headers = { Authorization: `Bearer ${token}` };

  const fetchPresets = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/boq-ai-presets`, { headers });
      setPresets(r.data?.presets || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load presets');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPresets(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let items = presets;
    // iter-383 — category legend chip filter. Applied before the text
    // search so the search still narrows within a picked category.
    if (categoryFilter) {
      const cat = categoryFilter.toLowerCase();
      items = items.filter((p) => (p.main_category_name || '').toLowerCase() === cat);
    }
    // iter-386u-bd — filter by material category (Hardware/Finishes/…)
    if (materialCategoryFilter) {
      items = items.filter((p) => (p.materials || []).some((m) => (m.category || 'Uncategorised') === materialCategoryFilter));
    }
    if (!q) return items;
    return items.filter((p) => (
      p.name?.toLowerCase().includes(q)
      || p.task_title?.toLowerCase().includes(q)
      || p.main_category_name?.toLowerCase().includes(q)
      || p.sub_category_name?.toLowerCase().includes(q)
    ));
  }, [presets, search, categoryFilter, materialCategoryFilter]);

  // iter-386u-bd — material-category chips (Hardware/Finishes/…)
  const materialChips = useMemo(() => {
    const map = new Map();
    presets.forEach((p) => {
      (p.materials || []).forEach((m) => {
        const cat = (m.category || 'Uncategorised').trim();
        if (!cat) return;
        map.set(cat, (map.get(cat) || 0) + 1);
      });
    });
    return Array.from(map.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
  }, [presets]);

  // iter-383 — Build the category legend from the preset library itself
  // (option 5-a+c). Only categories with at least one preset are shown,
  // sorted by count desc.
  const categoryChips = useMemo(() => {
    const map = new Map();
    presets.forEach((p) => {
      const name = (p.main_category_name || '').trim();
      if (!name) return;
      map.set(name, (map.get(name) || 0) + 1);
    });
    return Array.from(map.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
  }, [presets]);

  const openEdit = (p) => {
    setEditing(p);
    setDraft({
      name: p.name || '',
      task_title: p.task_title || '',
      description: p.description || '',
      main_category_id: p.main_category_id || null,
      main_category_name: p.main_category_name || '',
      sub_category_id: p.sub_category_id || null,
      sub_category_name: p.sub_category_name || '',
      unit: p.unit || 'NOS',
      quantity: p.quantity ?? 1,
      vendor_rate: p.vendor_rate ?? 0,
      client_rate: p.client_rate ?? 0,
      gst_percent: p.gst_percent ?? 18,
      remarks: p.remarks || '',
      materials: Array.isArray(p.materials) ? p.materials.map((m) => ({
        key: m.key || '',
        label: m.label || '',
        spec: m.spec || '',
        category: m.category || '',
        unit: m.unit || '',
        rate: Number(m.rate) || 0,
        client_rate: Number(m.client_rate) || 0,
      })) : [],
    });
  };
  const closeEdit = () => { setEditing(null); setDraft(emptyDraft); };

  const saveDraft = async () => {
    if (!editing) return;
    if (draft.name.trim().length < 2) { toast.error('Name is too short'); return; }
    setSaving(true);
    try {
      // iter-338z7 — the "__NEW_FROM_IMPORT__" sentinel means we're creating
      // a brand-new preset from an imported BOQ line, not editing an existing.
      if (editing.id === '__NEW_FROM_IMPORT__') {
        const r = await axios.post(`${API}/boq-ai-presets`, draft, { headers });
        const created = r.data?.preset;
        setPresets((prev) => [created, ...prev.filter((p) => p.id !== created.id)]);
        toast.success(r.data?.updated
          ? `Preset "${created.name}" updated from BOQ`
          : `Imported "${created.name}" from BOQ`);
      } else {
        const r = await axios.put(`${API}/boq-ai-presets/${editing.id}`, draft, { headers });
        const updated = r.data?.preset;
        setPresets((prev) => prev.map((p) => (p.id === editing.id ? updated : p)));
        toast.success(`Preset "${updated.name}" updated`);
      }
      closeEdit();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const toggleFav = async (p) => {
    try {
      const r = await axios.post(`${API}/boq-ai-presets/${p.id}/toggle-favourite`, {}, { headers });
      setPresets((prev) => prev
        .map((row) => (row.id === p.id ? { ...row, is_favourite: r.data?.is_favourite } : row))
        // keep favourites-first order after toggling
        .slice()
        .sort((a, b) => {
          const fa = (b.is_favourite ? 1 : 0) - (a.is_favourite ? 1 : 0);
          if (fa) return fa;
          return (b.usage_count || 0) - (a.usage_count || 0);
        }));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Toggle failed');
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete preset "${p.name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/boq-ai-presets/${p.id}`, { headers });
      setPresets((prev) => prev.filter((row) => row.id !== p.id));
      toast.success('Preset removed');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  const duplicate = async (p) => {
    try {
      const r = await axios.post(`${API}/boq-ai-presets/${p.id}/duplicate`, {}, { headers });
      const clone = r.data?.preset;
      if (!clone) return;
      // Prepend the clone so it's easy to find, then auto-open the edit
      // dialog so the user can immediately rename / tweak it.
      setPresets((prev) => [clone, ...prev]);
      toast.success(`Duplicated as "${clone.name}" — edit to customise`);
      openEdit(clone);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Duplicate failed');
    }
  };

  // iter-338z7 — Import from BOQ line ────────────────────────────────────────
  const fetchImportCandidates = async (q) => {
    setImportLoading(true);
    try {
      const r = await axios.get(`${API}/boq-ai-presets/import/candidates`, {
        headers,
        params: { search: q || '', limit: 30 },
      });
      setImportCandidates(r.data?.candidates || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load BOQ lines');
    } finally {
      setImportLoading(false);
    }
  };

  useEffect(() => {
    if (!importOpen) return;
    const t = setTimeout(() => fetchImportCandidates(importSearch), 250);
    return () => clearTimeout(t);
  }, [importOpen, importSearch]);

  const openImport = () => {
    setImportSearch('');
    setImportCandidates([]);
    setImportOpen(true);
  };

  // iter-386u-be — Materials Pack export / import
  const exportMaterialsPack = async () => {
    try {
      const r = await axios.get(`${API}/boq-ai-presets/materials-pack/export`, { headers });
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `bella-casa-materials-pack-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Exported ${r.data?.total || 0} materials`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Export failed'); }
  };
  const importMaterialsPack = async (e) => {
    const f = e.target?.files?.[0];
    if (!f) return;
    try {
      const text = await f.text();
      const pack = JSON.parse(text);
      const materials = Array.isArray(pack) ? pack : (pack.materials || []);
      if (!materials.length) { toast.error('No materials in file'); return; }
      const r = await axios.post(`${API}/boq-ai-presets/materials-pack/import`,
        { preset_name: pack.name || 'Imported Materials Pack', materials },
        { headers });
      toast.success(`Imported ${r.data?.imported || 0} materials · total in pack ${r.data?.materials_total || 0}`);
      await fetchPresets();
    } catch (err) { toast.error('Import failed: ' + (err.message || err.response?.data?.detail || 'invalid file')); }
    finally { e.target.value = ''; }
  };
  // iter-386u-bg — Publish current library as a shareable JSON pack.
  const publishAsPack = async () => {
    const name = window.prompt('Pack name?', 'Bella Casa Firm Pack');
    if (!name) return;
    try {
      const r = await axios.get(`${API}/boq-ai-presets/materials-pack/export`, { headers });
      const packaged = {
        name: name.trim(),
        description: `Curated by ${r.data?.exported_by || 'firm'}. Contains ${r.data?.total || 0} materials.`,
        exported_at: r.data?.exported_at,
        author: r.data?.exported_by,
        total: r.data?.total || 0,
        materials: r.data?.materials || [],
      };
      const blob = new Blob([JSON.stringify(packaged, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${name.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}-${new Date().toISOString().slice(0, 10)}.pack.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Published "${name}" as a shareable pack (${packaged.total} specs)`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Publish failed'); }
  };

  const importCandidateAsPreset = async (c) => {
    // Pre-fill the Edit dialog with a synthetic "draft" that isn't backed by
    // an existing preset — we intercept the Save handler to POST create.
    const suggestedName = (c.task_title || 'Imported preset').slice(0, 60);
    setImportOpen(false);
    setEditing({ id: '__NEW_FROM_IMPORT__', name: suggestedName });
    setDraft({
      name: suggestedName,
      task_title: c.task_title || '',
      description: c.description || '',
      main_category_name: c.main_category_name || '',
      sub_category_name: c.sub_category_name || '',
      unit: c.unit || 'NOS',
      quantity: c.quantity ?? 1,
      vendor_rate: c.vendor_rate ?? 0,
      client_rate: c.client_rate ?? 0,
      gst_percent: c.gst_percent ?? 18,
      remarks: c.remarks || '',
    });
  };

  // iter-338z9 — Bulk-apply a preset to multiple BOQs ────────────────────────
  const openBulkApply = async (p) => {
    setBulkPreset(p);
    setBulkSelected(new Set());
    setBulkInstruction('');
    setBulkLoading(true);
    try {
      const r = await axios.get(`${API}/boq-worksheets`, { headers });
      const rows = (r.data || []).filter((w) => w.status !== 'confirmed'
        && w.is_active !== false && !w.is_archived);
      setBulkWorksheets(rows);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load BOQs');
    } finally {
      setBulkLoading(false);
    }
  };
  const closeBulkApply = () => {
    setBulkPreset(null); setBulkWorksheets([]); setBulkSelected(new Set());
    setBulkInstruction('');
  };
  const toggleBulkSelect = (id) => {
    setBulkSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const submitBulkApply = async () => {
    if (!bulkPreset || bulkSelected.size === 0) { toast.error('Pick at least one BOQ'); return; }
    setBulkApplying(true);
    try {
      const r = await axios.post(
        `${API}/boq-ai-presets/${bulkPreset.id}/bulk-apply`,
        { worksheet_ids: Array.from(bulkSelected), instruction: bulkInstruction || null },
        { headers },
      );
      toast.success(`Applied "${r.data?.preset_name}" to ${r.data?.total_added} BOQ${(r.data?.total_added || 0) !== 1 ? 's' : ''}`);
      // bump the local usage_count for this preset so the UI reflects new state
      setPresets((prev) => prev.map((row) => (row.id === bulkPreset.id
        ? { ...row, usage_count: (row.usage_count || 0) + (r.data?.total_added || 0) }
        : row)));
      closeBulkApply();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk apply failed');
    } finally {
      setBulkApplying(false);
    }
  };

  const stats = useMemo(() => ({
    total: presets.length,
    favourites: presets.filter((p) => p.is_favourite).length,
    totalUses: presets.reduce((s, p) => s + (p.usage_count || 0), 0),
  }), [presets]);

  return (
    <Layout>
      <div className="max-w-7xl mx-auto p-4 md:p-6 space-y-4" data-testid="ai-preset-library-page">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold text-stone-900 flex items-center gap-2">
              <Sparkles size={22} className="text-violet-600" />
              AI Preset Library
            </h1>
            <p className="text-sm text-stone-600 mt-1">
              Reusable BOQ line templates saved from the AI Task Creator. Edit the specs, mark favourites, and every &quot;Custom&quot; preset in Edit BOQ picks up your changes instantly.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              size="sm"
              onClick={openImport}
              className="bg-violet-600 hover:bg-violet-700 text-white h-9"
              data-testid="preset-import-btn"
            >
              <Download size={14} className="mr-1.5" />
              Import from BOQ
            </Button>
            {/* iter-386u-be — Enterprise Materials Pack export/import */}
            <Button
              size="sm" variant="outline" className="h-9"
              onClick={exportMaterialsPack}
              data-testid="materials-pack-export-btn"
              title="Download every material across every preset as a JSON pack"
            >
              <Download size={14} className="mr-1.5" />
              Export pack
            </Button>
            <label className="h-9 inline-flex items-center gap-1.5 px-3 border border-stone-300 rounded-md bg-white text-xs text-stone-700 cursor-pointer hover:bg-stone-50" data-testid="materials-pack-import-label">
              <BookmarkPlus size={14} />
              Import pack
              <input type="file" accept="application/json" className="hidden" onChange={importMaterialsPack} data-testid="materials-pack-import-input" />
            </label>
            {/* iter-386u-bg — "Publish as Pack" downloads the tenant's whole
                library shaped like a marketplace pack so it can be shared
                with sub-tenants or peer firms. */}
            <Button
              size="sm" variant="outline" className="h-9"
              onClick={publishAsPack}
              data-testid="materials-pack-publish-btn"
              title="Export your library as a shareable Materials Pack"
            >
              <Sparkles size={14} className="mr-1.5 text-amber-500" />
              Publish as pack
            </Button>
            <div className="bg-white border border-stone-200 rounded-md px-3 py-2 text-xs text-stone-600 flex items-center gap-2" data-testid="preset-stats-total">
              <BookmarkPlus size={14} className="text-stone-400" />
              <span className="font-semibold text-stone-900">{stats.total}</span> presets
            </div>
            <div className="bg-white border border-stone-200 rounded-md px-3 py-2 text-xs text-stone-600 flex items-center gap-2" data-testid="preset-stats-fav">
              <Star size={14} className="text-amber-500" />
              <span className="font-semibold text-stone-900">{stats.favourites}</span> favourites
            </div>
            <div className="bg-white border border-stone-200 rounded-md px-3 py-2 text-xs text-stone-600 flex items-center gap-2" data-testid="preset-stats-uses">
              <TrendingUp size={14} className="text-emerald-500" />
              <span className="font-semibold text-stone-900">{stats.totalUses}</span> uses
            </div>
          </div>
        </div>

        {/* iter-386u-bf — Tab switch: Presets / Marketplace */}
        <div className="flex gap-2 border-b border-stone-200" data-testid="preset-library-tabs">
          {['presets', 'marketplace'].map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-4 py-2 text-sm capitalize border-b-2 transition ${
                tab === t ? 'border-violet-600 text-violet-700 font-semibold' : 'border-transparent text-stone-500 hover:text-stone-700'
              }`}
              data-testid={`preset-library-tab-${t}`}
            >{t === 'presets' ? 'My Presets' : 'Marketplace'}</button>
          ))}
        </div>

        {tab === 'marketplace' && (
          <MarketplacePane
            packs={packs}
            installing={installingPack}
            onInstall={installPack}
            onReload={loadPacks}
            onCreatePack={async () => {
              const name = window.prompt('Pack name?');
              if (!name) return;
              const description = window.prompt('Short description?') || '';
              // Seed with the tenant's current library so admin can prune from Edit BOQ later
              try {
                const exp = await axios.get(`${API}/boq-ai-presets/materials-pack/export`, { headers });
                const r = await axios.post(`${API}/boq-ai-presets/marketplace/packs`, {
                  name: name.trim(), description, author: '', materials: exp.data?.materials || [],
                }, { headers });
                toast.success(`Pack "${name}" saved · ${r.data?.total} specs`);
                await loadPacks();
              } catch (e) { toast.error(e.response?.data?.detail || 'Create failed'); }
            }}
          />
        )}

        {tab === 'presets' && (<>

        {/* iter-383 — Category legend. Only shows chips for main
            categories with at least one saved preset. Click a chip to
            filter the grid; click again (or hit Clear) to reset. */}
        {categoryChips.length > 0 && (
          <div className="bg-white border border-stone-200 rounded-md px-3 py-2 flex flex-wrap items-center gap-1.5" data-testid="preset-category-legend">
            <span className="text-[11px] text-stone-500 mr-1">Filter by category:</span>
            {categoryChips.map(({ name, count }) => {
              const active = categoryFilter === name;
              return (
                <button
                  key={name}
                  type="button"
                  onClick={() => setCategoryFilter(active ? '' : name)}
                  data-testid={`preset-category-chip-${name.toLowerCase().replace(/\s+/g, '-')}`}
                  className={
                    active
                      ? 'text-[11px] font-medium px-2 py-0.5 rounded-full border border-violet-500 bg-violet-600 text-white'
                      : 'text-[11px] font-medium px-2 py-0.5 rounded-full border border-stone-300 bg-white text-stone-700 hover:bg-violet-50 hover:border-violet-300'
                  }
                  title={`${count} preset${count !== 1 ? 's' : ''}`}
                >
                  {name}
                  <span className={active ? 'ml-1 text-[9px] opacity-80' : 'ml-1 text-[9px] text-stone-400'}>
                    {count}
                  </span>
                </button>
              );
            })}
            {categoryFilter && (
              <button
                type="button"
                onClick={() => setCategoryFilter('')}
                className="text-[11px] text-stone-500 hover:text-red-600 ml-1"
                data-testid="preset-category-clear-btn"
              >
                Clear
              </button>
            )}
          </div>
        )}

        {/* iter-386u-bd — Material category chips (Hardware / Finishes / …)
            filter presets that contain at least one material in that bucket. */}
        {materialChips.length > 0 && (
          <div className="bg-white border border-stone-200 rounded-md px-3 py-2 flex flex-wrap items-center gap-1.5" data-testid="preset-material-legend">
            <span className="text-[11px] text-stone-500 mr-1">Materials:</span>
            {materialChips.map(({ name, count }) => {
              const active = materialCategoryFilter === name;
              return (
                <button
                  key={name}
                  type="button"
                  onClick={() => setMaterialCategoryFilter(active ? '' : name)}
                  data-testid={`preset-material-chip-${name.toLowerCase().replace(/\s+/g, '-')}`}
                  className={
                    active
                      ? 'text-[11px] font-medium px-2 py-0.5 rounded-full border border-emerald-500 bg-emerald-600 text-white'
                      : 'text-[11px] font-medium px-2 py-0.5 rounded-full border border-stone-300 bg-white text-stone-700 hover:bg-emerald-50 hover:border-emerald-300'
                  }
                >
                  {name}
                  <span className={active ? 'ml-1 text-[9px] opacity-80' : 'ml-1 text-[9px] text-stone-400'}>{count}</span>
                </button>
              );
            })}
            {materialCategoryFilter && (
              <button
                type="button"
                onClick={() => setMaterialCategoryFilter('')}
                className="text-[11px] text-stone-500 hover:text-red-600 ml-1"
                data-testid="preset-material-clear-btn"
              >Clear</button>
            )}
          </div>
        )}

        <div className="relative max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 pointer-events-none" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search presets by name, title or category…"
            className="pl-9 bg-white text-stone-900"
            data-testid="preset-search-input"
          />
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-sm text-stone-500 py-12 justify-center">
            <Loader2 size={16} className="animate-spin" /> Loading presets…
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-white border border-dashed border-stone-300 rounded-lg py-16 text-center" data-testid="preset-empty-state">
            <Sparkles size={28} className="text-violet-400 mx-auto mb-3" />
            <div className="text-sm font-medium text-stone-900">No presets yet</div>
            <div className="text-xs text-stone-500 mt-1 max-w-md mx-auto">
              Open the AI Task Creator inside Edit BOQ, generate a line item, then hit the bookmark icon to save it here for reuse.
            </div>
          </div>
        ) : (
          <div className="bg-white border border-stone-200 rounded-lg overflow-hidden">
            <div className="grid grid-cols-12 gap-3 px-4 py-2.5 bg-stone-50 border-b border-stone-200 text-[10px] uppercase tracking-wider text-stone-500 font-semibold">
              <div className="col-span-3">Preset Name</div>
              <div className="col-span-2">Category</div>
              <div className="col-span-1 text-center">Materials</div>
              <div className="col-span-1 text-right">Unit / Qty</div>
              <div className="col-span-2 text-right">Rates (Vendor / Client)</div>
              <div className="col-span-1 text-right">Uses</div>
              <div className="col-span-2 text-right">Actions</div>
            </div>
            {filtered.map((p) => (
              <div
                key={p.id}
                className="grid grid-cols-12 gap-3 px-4 py-3 border-b border-stone-100 hover:bg-stone-50 text-sm items-center"
                data-testid={`preset-row-${p.id}`}
              >
                <div className="col-span-3">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => toggleFav(p)}
                      className="text-stone-300 hover:text-amber-500"
                      title={p.is_favourite ? 'Unmark favourite' : 'Mark favourite'}
                      data-testid={`preset-fav-${p.id}`}
                    >
                      <Star
                        size={14}
                        className={p.is_favourite ? 'text-amber-500 fill-amber-400' : ''}
                      />
                    </button>
                    <div className="font-medium text-stone-900 truncate">{p.name}</div>
                  </div>
                  {p.task_title && (
                    <div className="text-[11px] text-stone-500 truncate mt-0.5 ml-6" title={p.task_title}>
                      {p.task_title}
                    </div>
                  )}
                  {p.last_used_at && (
                    <div className="text-[10px] text-stone-400 mt-1 ml-6 inline-flex items-center gap-1">
                      <Calendar size={10} /> Last used {formatDate(p.last_used_at)}
                    </div>
                  )}
                </div>
                <div className="col-span-2 text-xs text-stone-700">
                  <div>{p.main_category_name || <span className="text-stone-400">—</span>}</div>
                  {p.sub_category_name && (
                    <div className="text-[11px] text-stone-500 truncate">{p.sub_category_name}</div>
                  )}
                </div>
                <div className="col-span-1 text-center" data-testid={`preset-materials-count-${p.id}`}>
                  {(p.materials || []).length > 0 ? (
                    <Badge variant="secondary" className="bg-violet-50 text-violet-800 border border-violet-200 text-[11px]" title={(p.materials || []).map((m) => m.label).join(' · ')}>
                      {(p.materials || []).length} spec{(p.materials || []).length !== 1 ? 's' : ''}
                    </Badge>
                  ) : (
                    <span className="text-stone-300 text-[11px]">—</span>
                  )}
                </div>
                <div className="col-span-1 text-right text-xs">
                  <div className="text-stone-900 font-medium">{p.quantity} <span className="text-stone-500">{p.unit}</span></div>
                  <div className="text-[10px] text-stone-400">GST {p.gst_percent}%</div>
                </div>
                <div className="col-span-2 text-right text-xs font-mono">
                  <div className="text-stone-700">₹ {(p.vendor_rate || 0).toLocaleString('en-IN')}</div>
                  <div className="text-emerald-700">₹ {(p.client_rate || 0).toLocaleString('en-IN')}</div>
                </div>
                <div className="col-span-1 text-right">
                  <Badge variant="secondary" className="bg-violet-50 text-violet-700 border border-violet-100" data-testid={`preset-uses-${p.id}`}>
                    {p.usage_count || 0}
                  </Badge>
                </div>
                <div className="col-span-2 flex items-center justify-end gap-1">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => openBulkApply(p)}
                    className="text-stone-600 hover:text-violet-700 hover:bg-violet-50 h-8 px-2"
                    title="Bulk-apply to open BOQs"
                    data-testid={`preset-bulk-apply-${p.id}`}
                  >
                    <Send size={13} />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => duplicate(p)}
                    className="text-stone-600 hover:text-violet-700 hover:bg-violet-50 h-8 px-2"
                    title="Duplicate & Fork"
                    data-testid={`preset-duplicate-${p.id}`}
                  >
                    <Copy size={13} />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => openEdit(p)}
                    className="text-stone-600 hover:text-violet-700 hover:bg-violet-50 h-8 px-2"
                    data-testid={`preset-edit-${p.id}`}
                  >
                    <Pencil size={13} className="mr-1" /> Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => remove(p)}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 px-2"
                    data-testid={`preset-delete-${p.id}`}
                  >
                    <Trash2 size={13} />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
        </>)}
      </div>

      <Dialog open={!!editing} onOpenChange={(v) => !v && closeEdit()}>
        <DialogContent className="!max-w-[98vw] w-[98vw] max-h-[95vh] overflow-hidden flex flex-col" data-testid="preset-edit-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-stone-900">
              <Pencil size={16} className="text-violet-600" />
              {editing?.id === '__NEW_FROM_IMPORT__' ? 'Import as new preset' : 'Edit Preset'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 overflow-y-auto pr-1 flex-1">
            <div className="grid grid-cols-12 gap-3">
              <div className="col-span-8">
                <Label className="text-[10px] uppercase tracking-wider text-stone-500">Preset Name</Label>
                <Input
                  value={draft.name}
                  onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                  className="bg-white text-stone-900 mt-0.5"
                  data-testid="preset-edit-name"
                />
              </div>
              <div className="col-span-4">
                <Label className="text-[10px] uppercase tracking-wider text-stone-500">Task Title Template</Label>
                <Input
                  value={draft.task_title}
                  onChange={(e) => setDraft((d) => ({ ...d, task_title: e.target.value }))}
                  className="bg-white text-stone-900 mt-0.5"
                  data-testid="preset-edit-title"
                />
              </div>
            </div>
            <div className="grid grid-cols-12 gap-3">
              <div className="col-span-8">
                <CategoryDropdown
                  mainId={draft.main_category_id}
                  mainName={draft.main_category_name}
                  subId={draft.sub_category_id}
                  subName={draft.sub_category_name}
                  onChange={(next) => setDraft((d) => ({ ...d, ...next }))}
                  testIdPrefix="preset-cat"
                />
              </div>
              <div className="col-span-2">
                <Label className="text-[10px] uppercase tracking-wider text-stone-500">Unit</Label>
                <div className="mt-0.5">
                  <UnitSelect
                    value={draft.unit}
                    onChange={(v) => setDraft((d) => ({ ...d, unit: v }))}
                    testId="preset-unit-select"
                  />
                </div>
              </div>
              <div className="col-span-2">
                <Label className="text-[10px] uppercase tracking-wider text-stone-500">Default Qty</Label>
                <Input
                  type="number"
                  value={draft.quantity}
                  onChange={(e) => setDraft((d) => ({ ...d, quantity: parseFloat(e.target.value) || 0 }))}
                  className="bg-white text-stone-900 mt-0.5"
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-stone-500">Vendor Rate ₹</Label>
                <Input
                  type="number"
                  value={draft.vendor_rate}
                  onChange={(e) => setDraft((d) => ({ ...d, vendor_rate: parseFloat(e.target.value) || 0 }))}
                  className="bg-white text-stone-900 mt-0.5"
                />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-stone-500">Client Rate ₹</Label>
                <Input
                  type="number"
                  value={draft.client_rate}
                  onChange={(e) => setDraft((d) => ({ ...d, client_rate: parseFloat(e.target.value) || 0 }))}
                  className="bg-white text-stone-900 mt-0.5"
                />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-stone-500">GST %</Label>
                <Input
                  type="number"
                  value={draft.gst_percent}
                  onChange={(e) => setDraft((d) => ({ ...d, gst_percent: parseFloat(e.target.value) || 0 }))}
                  className="bg-white text-stone-900 mt-0.5"
                />
              </div>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-stone-500">Description Template</Label>
              <Textarea
                rows={12}
                value={draft.description}
                onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
                className="bg-stone-50 text-stone-900 mt-0.5 text-xs font-mono whitespace-pre-wrap"
                data-testid="preset-edit-description"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-stone-500">Remarks</Label>
              <Textarea
                rows={2}
                value={draft.remarks}
                onChange={(e) => setDraft((d) => ({ ...d, remarks: e.target.value }))}
                className="bg-white text-stone-900 mt-0.5 text-xs"
              />
            </div>
            {/* iter-386u-bc — Materials & Technical Specs editor */}
            <MaterialsEditor
              materials={draft.materials}
              onChange={(next) => setDraft((d) => ({ ...d, materials: next }))}
            />
          </div>
          <DialogFooter className="pt-2 border-t">
            <Button variant="outline" onClick={closeEdit} disabled={saving}>Cancel</Button>
            <Button
              onClick={saveDraft}
              disabled={saving}
              className="bg-violet-600 hover:bg-violet-700 text-white"
              data-testid="preset-edit-save"
            >
              {saving ? <Loader2 size={14} className="animate-spin mr-1" /> : <Save size={14} className="mr-1" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* iter-338z7 — Import from BOQ line */}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="max-w-4xl w-[95vw] max-h-[85vh] overflow-hidden flex flex-col" data-testid="preset-import-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-stone-900">
              <Download size={16} className="text-violet-600" />
              Import a BOQ line as a preset
            </DialogTitle>
          </DialogHeader>
          <div className="text-xs text-stone-600 bg-violet-50 border border-violet-200 rounded-md p-2.5">
            Search across every active BOQ in your studio, then pick a line to convert into a reusable preset. The line&apos;s title, description, category, unit, quantity, rates and GST are copied — no LLM call needed.
          </div>
          <div className="relative mt-2">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 pointer-events-none" />
            <Input
              value={importSearch}
              onChange={(e) => setImportSearch(e.target.value)}
              placeholder="Search by task title, description or category…"
              className="pl-9 bg-white text-stone-900"
              autoFocus
              data-testid="preset-import-search"
            />
          </div>
          <div className="overflow-y-auto flex-1 mt-2 border border-stone-200 rounded-md">
            {importLoading ? (
              <div className="flex items-center gap-2 text-sm text-stone-500 py-10 justify-center">
                <Loader2 size={16} className="animate-spin" /> Searching BOQ lines…
              </div>
            ) : importCandidates.length === 0 ? (
              <div className="text-center py-10 text-sm text-stone-500">
                {importSearch.trim()
                  ? 'No matching BOQ lines found.'
                  : 'Start typing to search across your BOQ worksheets, or leave blank to see the most recent lines.'}
              </div>
            ) : (
              <div className="divide-y divide-stone-100">
                {importCandidates.map((c, i) => (
                  <button
                    key={`${c.worksheet_id}-${c.item_id || i}`}
                    onClick={() => importCandidateAsPreset(c)}
                    className="w-full text-left px-3 py-2.5 hover:bg-violet-50 transition-colors flex items-start gap-3"
                    data-testid={`preset-import-candidate-${i}`}
                  >
                    <FileSpreadsheet size={16} className="text-violet-500 mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-stone-900 text-sm truncate">{c.task_title || '(untitled line)'}</div>
                      <div className="text-[11px] text-stone-500 mt-0.5 flex items-center gap-1.5 flex-wrap">
                        {c.project_name && <span className="truncate max-w-[200px]">{c.project_name}</span>}
                        {c.project_name && <span className="text-stone-300">·</span>}
                        <span className="font-mono">{c.worksheet_name}</span>
                        {c.main_category_name && (
                          <>
                            <span className="text-stone-300">·</span>
                            <span>{c.main_category_name}{c.sub_category_name ? ` / ${c.sub_category_name}` : ''}</span>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="text-right text-xs shrink-0">
                      <div className="text-stone-900 font-medium">{c.quantity} <span className="text-stone-500">{c.unit}</span></div>
                      <div className="text-stone-500 font-mono">₹ {(c.vendor_rate || 0).toLocaleString('en-IN')} / <span className="text-emerald-700">{(c.client_rate || 0).toLocaleString('en-IN')}</span></div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
          <DialogFooter className="pt-2 border-t">
            <Button variant="outline" onClick={() => setImportOpen(false)}>Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* iter-338z9 — Bulk-apply preset to multiple BOQs */}
      <Dialog open={!!bulkPreset} onOpenChange={(v) => !v && closeBulkApply()}>
        <DialogContent className="max-w-3xl w-[95vw] max-h-[85vh] overflow-hidden flex flex-col" data-testid="preset-bulk-apply-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-stone-900">
              <Send size={16} className="text-violet-600" />
              Bulk-apply preset — {bulkPreset?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="text-xs text-stone-600 bg-violet-50 border border-violet-200 rounded-md p-2.5">
            Tick the BOQs where this preset should be appended as a new line item. Optionally add dimensions (one per BOQ, comma-separated — e.g. <em>8x6, 10x8</em>) to override the default quantity per BOQ. Only draft / open BOQs are shown; confirmed &amp; archived BOQs are hidden.
          </div>
          <div className="mt-2">
            <Label className="text-[10px] uppercase tracking-wider text-stone-500">
              Dimensions per BOQ (optional)
            </Label>
            <Input
              value={bulkInstruction}
              onChange={(e) => setBulkInstruction(e.target.value)}
              placeholder="e.g. 8x6, 10x8, 12x8"
              className="bg-white text-stone-900 mt-0.5 text-sm"
              data-testid="preset-bulk-apply-instruction"
            />
            <div className="text-[10px] text-stone-400 mt-1">
              Matched positionally to selected BOQs. Missing values fall back to the preset&apos;s default qty.
            </div>
          </div>
          <div className="overflow-y-auto flex-1 mt-2 border border-stone-200 rounded-md">
            {bulkLoading ? (
              <div className="flex items-center gap-2 text-sm text-stone-500 py-10 justify-center">
                <Loader2 size={16} className="animate-spin" /> Loading open BOQs…
              </div>
            ) : bulkWorksheets.length === 0 ? (
              <div className="text-center py-10 text-sm text-stone-500">
                No open BOQs to apply this preset to.
              </div>
            ) : (
              <div className="divide-y divide-stone-100">
                {bulkWorksheets.map((w) => {
                  const isSel = bulkSelected.has(w.id);
                  return (
                    <label
                      key={w.id}
                      className={`flex items-start gap-3 px-3 py-2.5 cursor-pointer transition-colors ${isSel ? 'bg-violet-50' : 'hover:bg-stone-50'}`}
                      data-testid={`preset-bulk-apply-ws-${w.id}`}
                    >
                      <input
                        type="checkbox"
                        checked={isSel}
                        onChange={() => toggleBulkSelect(w.id)}
                        className="mt-1 h-4 w-4 accent-violet-600"
                        data-testid={`preset-bulk-apply-ws-cb-${w.id}`}
                      />
                      <FileSpreadsheet size={16} className="text-violet-500 mt-0.5 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-stone-900 text-sm truncate">
                          {w.display_name || w.worksheet_number}
                        </div>
                        <div className="text-[11px] text-stone-500 mt-0.5 truncate">
                          {w.project_name || '(no project)'}
                          {w.worksheet_number && w.display_name && (
                            <span className="ml-2 font-mono">{w.worksheet_number}</span>
                          )}
                        </div>
                      </div>
                      <div className="text-right text-xs shrink-0">
                        <div className="text-stone-500 uppercase tracking-wider text-[10px]">
                          {w.status || 'draft'}
                        </div>
                        <div className="text-stone-600 mt-0.5">
                          {(w.items || []).length} lines
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
            )}
          </div>
          <DialogFooter className="pt-2 border-t flex items-center justify-between gap-2">
            <div className="text-xs text-stone-500 mr-auto">
              {bulkSelected.size} of {bulkWorksheets.length} BOQ{bulkWorksheets.length !== 1 ? 's' : ''} selected
            </div>
            <Button variant="outline" onClick={closeBulkApply} disabled={bulkApplying}>Cancel</Button>
            <Button
              onClick={submitBulkApply}
              disabled={bulkApplying || bulkSelected.size === 0}
              className="bg-violet-600 hover:bg-violet-700 text-white"
              data-testid="preset-bulk-apply-confirm"
            >
              {bulkApplying ? <Loader2 size={14} className="animate-spin mr-1" /> : <Send size={14} className="mr-1" />}
              Apply to {bulkSelected.size} BOQ{bulkSelected.size !== 1 ? 's' : ''}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}


// iter-386u-bf — Marketplace pane: curated Materials Packs installable in one click.
function MarketplacePane({ packs, installing, onInstall, onCreatePack, onReload }) {
  return (
    <div className="space-y-4" data-testid="marketplace-pane">
      <div className="flex items-center justify-between">
        <div className="text-[11px] text-stone-500">
          Curated by Bella Casa. Owner accounts can add or edit packs — every install pulls from the live database.
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={onReload} data-testid="marketplace-reload-btn">↻ Reload</Button>
          <Button size="sm" onClick={onCreatePack} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="marketplace-create-pack-btn">+ Create Pack</Button>
        </div>
      </div>
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {packs.length === 0 && (
          <div className="col-span-full text-center text-sm text-stone-400 py-16 italic">Loading curated packs…</div>
        )}
        {packs.map((p) => {
          const busy = installing === p.code;
          const catCount = new Set(p.materials.map((m) => m.category || 'Uncategorised')).size;
          return (
            <div key={p.code} className="border border-stone-200 rounded-xl bg-white p-4 flex flex-col" data-testid={`marketplace-pack-${p.code}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-stone-900">{p.name}</h3>
                  <div className="text-[10px] text-stone-400 uppercase tracking-wider mt-0.5">{p.author || 'Curated pack'}</div>
                </div>
                {p.installed ? (
                  <span className="text-[10px] uppercase font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded" data-testid={`marketplace-installed-${p.code}`}>Installed</span>
                ) : p.installed_count > 0 ? (
                  <span className="text-[10px] uppercase font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded" title={`${p.installed_count}/${p.total} specs already in your library`}>Partial</span>
                ) : (
                  <span className="text-[10px] uppercase font-semibold text-stone-500 bg-stone-50 border border-stone-200 px-2 py-0.5 rounded">New</span>
                )}
              </div>
              <p className="text-xs text-stone-600 mt-2 leading-snug flex-1">{p.description}</p>
              <div className="mt-3 flex items-center gap-3 text-[11px] text-stone-500">
                <span><strong className="text-stone-800">{p.total}</strong> specs</span>
                <span><strong className="text-stone-800">{catCount}</strong> categories</span>
              </div>
              <div className="mt-3 flex flex-wrap gap-1">
                {Array.from(new Set(p.materials.map((m) => m.category || 'Uncategorised'))).slice(0, 4).map((c) => (
                  <span key={c} className="text-[10px] font-medium px-1.5 py-0.5 rounded-full border border-stone-200 bg-stone-50 text-stone-600">{c}</span>
                ))}
              </div>
              <Button size="sm" onClick={() => onInstall(p.code)} disabled={busy}
                className={`mt-4 ${p.installed ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-violet-600 hover:bg-violet-700'} text-white`}
                data-testid={`marketplace-install-${p.code}`}
              >
                {busy ? 'Installing…' : p.installed ? 'Re-install / Update' : `Install ${p.total} specs`}
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );
}


// iter-386u-bc — Structured Materials & Technical Specs table.
// Each row: Category · Name (label) · Technical Spec (long) · Unit · Rate.
// The `key` field is auto-slugged from `label` on blur so users never have to
// think about it — dedupe happens server-side on the materials-library flat.
function MaterialsEditor({ materials, onChange }) {
  const rows = materials || [];
  const update = (idx, patch) => {
    const next = rows.slice();
    next[idx] = { ...next[idx], ...patch };
    onChange(next);
  };
  const remove = (idx) => onChange(rows.filter((_, i) => i !== idx));
  const add = () => onChange([
    ...rows,
    { key: '', label: '', spec: '', category: '', unit: 'NOS', rate: 0, client_rate: 0 },
  ]);
  const autoKey = (label) => (label || '')
    .toLowerCase().trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .slice(0, 40);

  return (
    <div className="border border-violet-200 rounded-lg bg-violet-50/30 p-3" data-testid="preset-materials-editor">
      <div className="flex items-center justify-between gap-2 mb-2">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-violet-700 font-semibold">Materials &amp; Technical Specs</div>
          <p className="text-[10px] text-stone-500 mt-0.5">Each row becomes a draggable chip in the AI Task Creator&apos;s pivot pane.</p>
        </div>
        <Button size="sm" variant="outline" onClick={add} data-testid="preset-materials-add">+ Add row</Button>
      </div>
      {rows.length === 0 && (
        <div className="text-center text-[11px] text-stone-400 py-3 italic">
          No materials yet. Add rows for hardware, boards, finishes — anything you want to "pivot" into future BOQ lines.
        </div>
      )}
      {rows.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-stone-500">
                <th className="text-left px-1 py-1 font-semibold">Category</th>
                <th className="text-left px-1 py-1 font-semibold">Name</th>
                <th className="text-left px-1 py-1 font-semibold">Technical Spec</th>
                <th className="text-left px-1 py-1 font-semibold w-16">Unit</th>
                <th className="text-right px-1 py-1 font-semibold w-24">Vendor Rate</th>
                <th className="text-right px-1 py-1 font-semibold w-24">Client Rate</th>
                <th className="w-8" />
              </tr>
            </thead>
            <tbody>
              {rows.map((m, i) => (
                <tr key={i} className="align-top" data-testid={`preset-material-row-${i}`}>
                  <td className="px-1 py-1">
                    <input value={m.category || ''} onChange={(e) => update(i, { category: e.target.value })}
                      placeholder="Hardware" className="w-full text-xs border border-stone-200 rounded px-1.5 py-1 bg-white"
                      data-testid={`preset-material-category-${i}`} />
                  </td>
                  <td className="px-1 py-1">
                    <input value={m.label || ''} onChange={(e) => update(i, { label: e.target.value })}
                      onBlur={() => { if (!m.key && m.label) update(i, { key: autoKey(m.label) }); }}
                      placeholder="Soft-close hinge" className="w-full text-xs border border-stone-200 rounded px-1.5 py-1 bg-white"
                      data-testid={`preset-material-label-${i}`} />
                  </td>
                  <td className="px-1 py-1">
                    <textarea value={m.spec || ''} onChange={(e) => update(i, { spec: e.target.value })}
                      rows={2} placeholder="Hettich Sensys 8645i, 110°, nickel-plated"
                      className="w-full text-xs border border-stone-200 rounded px-1.5 py-1 bg-white font-mono"
                      data-testid={`preset-material-spec-${i}`} />
                  </td>
                  <td className="px-1 py-1">
                    <UnitSelect value={m.unit} onChange={(v) => update(i, { unit: v })}
                      className="!py-1" testId={`preset-material-unit-${i}`} />
                  </td>
                  <td className="px-1 py-1">
                    <input type="number" step="0.01" value={m.rate ?? 0} onChange={(e) => update(i, { rate: parseFloat(e.target.value) || 0 })}
                      className="w-full text-xs border border-stone-200 rounded px-1.5 py-1 bg-white text-right"
                      data-testid={`preset-material-rate-${i}`} />
                  </td>
                  <td className="px-1 py-1">
                    <input type="number" step="0.01" value={m.client_rate ?? 0} onChange={(e) => update(i, { client_rate: parseFloat(e.target.value) || 0 })}
                      className="w-full text-xs border border-stone-200 rounded px-1.5 py-1 bg-white text-right"
                      data-testid={`preset-material-client-rate-${i}`} />
                  </td>
                  <td className="px-1 py-1 text-right">
                    <button onClick={() => remove(i)} className="text-rose-500 hover:text-rose-700 text-xs" data-testid={`preset-material-remove-${i}`}>×</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
